import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  size = "md",
  showClose = true,
  fitScreen = false,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  showClose?: boolean;
  /** Cap the dialog at the screen height and scroll its body instead of the
   *  page, so its footer stays in view. For very tall forms only: a dropdown
   *  near the bottom of a capped body is clipped by the scroll area. */
  fitScreen?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const sizeClass = {
    sm: "max-w-sm",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
  }[size];

  return createPortal(
    // A dialog taller than the screen scrolls as a whole instead of being
    // centred off-screen, so its title and buttons remain reachable on phones
    // and in the dashboard frame. The overlay starts at the top and the page,
    // not the dialog body, scrolls so date pickers and dropdowns stay visible.
    <div className="fixed inset-0 z-50 overflow-y-auto overscroll-contain animate-fade-in" role="dialog" aria-modal="true">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onMouseDown={onClose} />
      {/* Outside-click: the wrapper lies over the backdrop and closes only
          when the press lands on its own empty space. */}
      <div
        className="relative flex min-h-full items-center justify-center p-4 sm:p-6"
        onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div
          className={cn(
            "relative w-full rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl shadow-shade/50 animate-slide-up",
            fitScreen && "flex max-h-[calc(100dvh-2rem)] flex-col",
            sizeClass
          )}
          // Stop click bubbling so clicks INSIDE the modal don't accidentally
          // hit the backdrop and close it.
          onMouseDown={(e) => e.stopPropagation()}
        >
          {(title || showClose) && (
            <div className="flex items-start justify-between gap-4 border-b border-zinc-800 px-5 py-4">
              <div className="min-w-0">
                {title && <h2 className="text-base font-semibold text-zinc-100 truncate">{title}</h2>}
                {description && <p className="mt-1 text-xs text-zinc-500">{description}</p>}
              </div>
              {showClose && (
                <button
                  type="button"
                  onClick={onClose}
                  className="-mr-1 -mt-1 inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-800/60 hover:text-zinc-200"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
          <div className={cn("px-5 py-4", fitScreen && "min-h-0 flex-1 overflow-y-auto")}>{children}</div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function ModalFooter({ children }: { children: ReactNode }) {
  return <div className="-mx-5 -mb-4 mt-5 flex items-center justify-end gap-2 border-t border-zinc-800 px-5 py-3">{children}</div>;
}

/* ─── Drawer (right-side, full-height) ─────────────────────────────────────── */
export function Drawer({
  open,
  onClose,
  title,
  children,
  width = "md",
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  width?: "sm" | "md" | "lg" | "xl";
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);
  if (!open) return null;
  const widthClass = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-xl",
    xl: "max-w-3xl",
  }[width];
  return createPortal(
    <div className="fixed inset-0 z-50 flex animate-fade-in" role="dialog" aria-modal="true">
      <div className="flex-1 bg-black/60 backdrop-blur-sm" onMouseDown={onClose} />
      <div className={cn("flex h-full w-full flex-col border-l border-zinc-800 bg-zinc-950 shadow-2xl shadow-shade/50 animate-slide-in-right", widthClass)}>
        {title !== undefined && (
          <div className="flex items-center justify-between gap-4 border-b border-zinc-800 px-5 py-4">
            <h2 className="text-base font-semibold text-zinc-100 truncate">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-800/60 hover:text-zinc-200"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>,
    document.body
  );
}
